import { useEffect, useRef } from 'react';
import { ACCEPT_INVITATION_LABELS_ID } from '../../config/constants';
import { useAuth0 } from '@lucid/auth';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Typography, Iconography, Box } from '@lucid/lego';
import { useStore } from '../../store/store';
import { useLabels } from '../../hooks/useLabels';
import { convertToContentfulLocale } from '../../utils/tools';
import { authDebugInfo, authDebugWarn, redactValue } from '../../utils/authDebug';
import styles from './AcceptInvitation.module.scss';

/**
 * Maximum allowed length for query parameters to prevent potential attacks
 */
const MAX_PARAM_LENGTH = 500;

/**
 * Validates that a query parameter is a non-empty string within safe length limits
 *
 * @param value - The query parameter value to validate
 * @returns True if the value is a valid, safe string
 */
const isValidParam = (value: string | null): boolean => {
  if (!value || typeof value !== 'string') return false;
  const trimmedValue = value.trim();
  if (trimmedValue.length === 0 || trimmedValue.length > MAX_PARAM_LENGTH) return false;
  // Basic sanitization: ensure no suspicious characters
  if (/<script|javascript:|data:|vbscript:/i.test(trimmedValue)) return false;
  return true;
};

/**
 * AcceptInvitation Page Component
 *
 * @component
 * Handles Auth0 organization invitation acceptance flow with security validations.
 *
 * **Security Features:**
 * - Input validation on all query parameters
 * - XSS prevention through parameter sanitization
 * - No sensitive data logging
 * - Fixed redirect destination (no open redirect vulnerability)
 * - Auth0 PKCE flow for secure authentication
 *
 * **Flow:**
 * 1. User clicks invitation link from email
 * 2. Lands here with invitation, organization, and organization_name params
 * 3. Parameters are validated for security
 * 4. Redirects to Auth0 Universal Login with organization context
 * 5. Auth0 handles password setup for first-time users
 * 6. Auth0 redirects back to app after successful authentication
 *
 * @example
 * ```tsx
 * // Invitation link format:
 * // https://business.stage.dslmotors.com/accept-invitation?invitation=TOKEN&organization=org_XXX&organization_name=NAME
 *
 * <Route path="/accept-invitation" element={<AcceptInvitation />} />
 * ```
 *
 * @returns Loading UI while processing invitation or redirecting
 */
const AcceptInvitation = () => {
  const { loginWithRedirect, isAuthenticated, isLoading } = useAuth0();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const locale = useStore((state) => state.locale);
  const isRedirecting = useRef(false);

  const { data: labels } = useLabels({
    id: ACCEPT_INVITATION_LABELS_ID,
    locale: convertToContentfulLocale(locale),
  });

  useEffect(() => {
    const invitation = searchParams.get('invitation');
    const organization = searchParams.get('organization');
    const organizationName = searchParams.get('organization_name');

    const isInvitationValid = isValidParam(invitation);
    const isOrganizationValid = isValidParam(organization);

    authDebugInfo('AcceptInvitation effect start', {
      pathname: window.location.pathname,
      invitation: redactValue(invitation),
      organization: redactValue(organization),
      organizationName: redactValue(organizationName),
      isInvitationValid,
      isOrganizationValid,
      isAuthenticated,
    });

    if (!isInvitationValid || !isOrganizationValid) {
      authDebugWarn('Invitation params invalid or missing; routing to standard auth entrypoint', {
        invitation: redactValue(invitation),
        organization: redactValue(organization),
        isAuthenticated,
      });

      // Wait for Auth0 to finish loading before redirecting — isAuthenticated defaults
      // to false during initialisation, so acting on it early would wrongly send
      // an authenticated user to /login.
      if (!isLoading) {
        isAuthenticated ? navigate('/', { replace: true }) : navigate('/login', { replace: true });
      }
      return;
    }

    // For the invitation flow we don't wait for isLoading to settle.
    // The user is always unauthenticated (coming from an email link), and
    // isLoading can get permanently stuck on custom Auth0 domains where the
    // silent-auth iframe is blocked by third-party cookie restrictions.
    // auth0-react v2 does not throw when loginWithRedirect is called during isLoading.

    // Prevent duplicate loginWithRedirect calls — on deployed environments with
    // custom Auth0 domains, the effect can re-fire (due to Auth0 SDK state changes)
    // before the browser redirect completes, causing competing calls that cancel
    // each other out and leave the user stuck.
    if (isRedirecting.current) {
      authDebugInfo('Skipping duplicate loginWithRedirect call during invitation processing');
      return;
    }
    isRedirecting.current = true;

    authDebugInfo('Calling loginWithRedirect for invitation acceptance', {
      invitation: redactValue(invitation),
      organization: redactValue(organization),
      returnTo: '/',
    });

    loginWithRedirect({
      authorizationParams: {
        organization: organization as string,
        invitation: invitation as string,
      },
      appState: { returnTo: '/' },
    }).catch((error) => {
      console.error('[AcceptInvitation] Error redirecting to Auth0:', error.message);
      authDebugWarn('loginWithRedirect failed during invitation flow', {
        message: error?.message ?? 'unknown',
      });
      isRedirecting.current = false;
      navigate('/login', { replace: true });
    });
  }, [isAuthenticated, isLoading, loginWithRedirect, navigate, searchParams]);

  return (
    <Box className={styles.login} testId="accept-invitation">
      <Box className={styles.container}>
        <Box className={styles.invitationBox}>
          <Box className={styles.logo}>
            <Iconography category="brand" name="logo" color="black" size={32} />
            <Iconography category="brand" name="bear" color="black" size={32} />
          </Box>
          <Box className={styles.title}>
            <Typography variant="headline" size="medium" color="black" align="center">
              {labels?.['invitation.accept.title'] ?? 'Partner Portal'}
            </Typography>
          </Box>
          <Box className={styles.subtitle}>
            <Typography variant="body" size="medium" color="gray-8" align="center">
              {labels?.['invitation.accept.subtitle'] ??
                'Please wait while we redirect you to set up your account.'}
            </Typography>
          </Box>
          <Box className={styles.processing}>
            <Typography variant="body" size="medium" color="gray-8" align="center">
              {labels?.['invitation.accept.processing'] ?? 'Processing your invitation...'}
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default AcceptInvitation;
